// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// FAV Inbox (#630) — pure helpers for the left pane's Inbox tab and the dimmed grid
// tiles. Pure so the tests import exactly what the components run.
// Plan: docs/fav-inbox.md. Server side: convex/scheduler/favInbox.ts.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * The FAV Inbox (tabs, dimmed bookings, FAV 2.0 ghosts, Unscheduled, Why?, red boxes) is shown
 * only to users whose User record has inboxFlag === true (Leland 2026-10-16: "Brandon & I are true,
 * everyone else = false … no tabs or Inbox until we are ready to train users on it"). On EVERY
 * build, PROD included (Leland 2026-10-24: FAV 2.0 goes live for chosen offices/users — "Still
 * gated. Same user settings so that only Brandon and I see fav v2 stuff"). Was: never on PROD.
 * Needs Convex. `appEnv` is accepted but no longer decides anything.
 */
// eslint-disable-next-line no-unused-vars
export function favInboxEnabled({ appEnv, convexConfigured, inboxFlag }) {
  return Boolean(convexConfigured) && inboxFlag === true;
}

/** The job's id as the Inbox stores it (ActiveQueue _id hex, lower-case). */
export function jobKey(project) {
  return String(project?.mongoId || project?.id || "").toLowerCase();
}

/**
 * #755: FAV 2.0's "couldn't schedule" answers by job id (lower-cased, like jobKey) — the tiles
 * with a red box and an ⓘ. rows: scheduler/fav2Log:skipsForLocation.
 */
export function buildSkipsByJob(rows) {
  const m = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    const id = String((r && r.jobId) || "").toLowerCase();
    if (id && r.why) m.set(id, r);
  }
  return m;
}

/** Set of job ids with an open (pending or deferred) Inbox row — the tiles to dim. */
export function openJobIds(rows) {
  return new Set((Array.isArray(rows) ? rows : []).map((r) => String(r.jobId || "").toLowerCase()).filter(Boolean));
}

/**
 * Reassign is offered only when FAV assigned a crew AND that crew is still on the job
 * (Leland: "only shown if a crew was auto-assigned"). FAV never assigns one today.
 */
export function canReassign(row, project) {
  const booked = String(row?.bookedInstaller ?? "").trim();
  return booked !== "" && String(project?.installer ?? "").trim() === booked;
}

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "2026-11-10" → "11/10/2026" (the grid's date format), else "". */
export function isoToMdy(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${+m[2]}/${+m[3]}/${m[1]}` : "";
}

/**
 * The drop target for each button — the same ids the grid's own cells use, so the
 * button runs exactly the drag-drop path (same windows, same FileMaker update):
 *   Unschedule → "Unassigned_RTS" (the RTS pane)
 *   Reassign   → "Unassigned_<Day>_<M/D/YYYY>" (the Unassigned row on the booked date)
 */
export const UNSCHEDULE_DROP_ID = "Unassigned_RTS";
export function reassignDropId(bookedDateIso) {
  const mdy = isoToMdy(bookedDateIso);
  if (!mdy) return "";
  const [y, m, d] = bookedDateIso.split("-").map(Number);
  return `Unassigned_${DAY_ABBR[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}_${mdy}`;
}

/**
 * The Inbox's three accordions (Leland 2026-10-13), top to bottom:
 *   Assigned   — FAV booked a crew (FAV v2; empty today)
 *   Unassigned — FAV booked the day only (every booking today)
 *   Deferred   — seen, still unapproved
 */
export function groupInbox(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const pending = list.filter((r) => r.status === "pending");
  return {
    assigned: pending.filter((r) => String(r.bookedInstaller ?? "").trim() !== ""),
    unassigned: pending.filter((r) => String(r.bookedInstaller ?? "").trim() === ""),
    deferred: list.filter((r) => r.status === "deferred"),
  };
}

/** "2026-11-10" → a local Date on that day (noon, so no DST/UTC edge), else null. */
export function isoToLocalDate(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], 12) : null;
}

/**
 * The Monday (local 00:00) of the Mon–Sun grid week that CONTAINS the date. Not
 * weekDates.getMondayOf, which sends a Sunday to the FOLLOWING Monday — a Sunday
 * booking must jump to the week it's shown in.
 */
export function gridWeekMondayOf(iso) {
  const d = isoToLocalDate(iso);
  if (!d) return null;
  const back = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - back);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

/**
 * Where to put the row's drop-down menu: below the ▾ button if it fits, otherwise
 * above it, and always fully inside the window (a menu hanging off the bottom of the
 * screen needs a scroll to reach — and a scroll closes it). Pure; `btn` is a
 * DOMRect-like {top, bottom, left}, `menu` {width, height}, `view` {width, height}.
 */
export function menuPosition(btn, menu, view, gap = 2, margin = 4) {
  const below = btn.bottom + gap;
  const above = btn.top - gap - menu.height;
  let top = below + menu.height <= view.height - margin ? below : above;
  top = Math.max(margin, Math.min(top, view.height - margin - menu.height));
  const left = Math.max(margin, Math.min(btn.left, view.width - margin - menu.width));
  return { top, left };
}

/**
 * The FAV 2.0 advice line (#642), wording agreed in PRED-028 / fav-2-contracts.md §2:
 * "FAV 2.0 would book: <crew>, <start>–<end>". Null unless the advice is "ok".
 */
export function fav2Line(why) {
  const f = why && why.fav2;
  // PRED-035: a "no suggestion" answer shows FAV 2.0's own first sentence (its why text),
  // e.g. "FAV 2.0 found nothing it could book: no crew at the office has every skill …".
  // Older skips stored no text → nothing shown (no made-up wording).
  if (f && f.status === "skipped") {
    const first = String(f.why || "").split("\n")[0].trim();
    return first || null;
  }
  if (!f || f.status !== "ok") return null;
  const md = (iso) => {
    const m = String(iso || "").match(/^\d{4}-(\d{2})-(\d{2})$/);
    return m ? `${+m[1]}/${+m[2]}` : "";
  };
  const crew = f.installerName || f.installer;
  const when = f.startDateISO === f.endDateISO ? md(f.startDateISO) : `${md(f.startDateISO)}–${md(f.endDateISO)}`;
  return `FAV 2.0 would book: ${crew}, ${when}`;
}

// - - - - - - - - - - Ghost bookings (#642, Leland 2026-10-13: "so Brandon can view,
// watch, get excited") — for each OPEN Inbox booking with FAV 2.0 advice, a faint
// "ghost" card in the advised crew's row on each advised working day. UAT-only, like
// the rest of the Inbox.

/** The advised working days (ISO). Older advice has no list: start → end, Sundays skipped. */
export function ghostDays(fav2) {
  if (!fav2 || fav2.status !== "ok") return [];
  if (Array.isArray(fav2.runDatesISO) && fav2.runDatesISO.length) return fav2.runDatesISO;
  const start = isoToLocalDate(fav2.startDateISO);
  const end = isoToLocalDate(fav2.endDateISO || fav2.startDateISO);
  if (!start || !end) return [];
  const out = [];
  for (const d = new Date(start); d <= end && out.length < 10; d.setDate(d.getDate() + 1)) {
    if (d.getDay() === 0) continue;
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  }
  return out;
}

/** Key for a grid cell: the crew's 7-character payroll + the grid's M/D/YYYY date. */
export const ghostCellKey = (installer, mdy) => `${installer}|${mdy}`;

/**
 * An advice-only proposal (fav2Log, FileMaker's reserve:false call — the job is NOT booked)
 * in the shape buildGhostsByCell reads (#652). TEMPORARY with the "Unscheduled" accordion.
 */
/**
 * The Inbox row's 👤 "worth a person's check" (PRED-032, Leland 2026-10-14: the Inbox shows
 * ONLY FAV 2.0). A row with a FAV 2.0 answer uses FAV 2.0's own flag + reasons (e.g.
 * long_wait); only rows from before FAV 2.0 fall back to the older Jev plan's flag.
 */
export function inboxNeedsPerson(why) {
  if (!why) return { flag: false, reasons: [] };
  if (why.fav2 && typeof why.fav2 === "object") {
    // PRED-035: "FAV 2.0 found no crew it could book" is the strongest reason for a person
    // to look — any skip except degraded data flags the row, with the skip reason.
    if (why.fav2.status === "skipped") {
      const reason = String(why.fav2.reason || "");
      return reason && reason !== "degraded" ? { flag: true, reasons: [reason] } : { flag: false, reasons: [] };
    }
    return { flag: why.fav2.needsPerson === true, reasons: Array.isArray(why.fav2.reasons) ? why.fav2.reasons : [] };
  }
  return { flag: why.needsPerson === true, reasons: Array.isArray(why.reasons) ? why.reasons : [] };
}

export function adviceAsInboxRow(a) {
  return {
    id: `advice-${a.id}`,
    jobId: a.jobId,
    kellerId: a.kellerId,
    status: "advice",
    adviceOnly: true,
    bookedDate: null,
    why: {
      fav2: {
        status: "ok",
        installer: a.installer,
        installerName: a.installerName,
        startDateISO: a.startDateISO,
        endDateISO: a.endDateISO,
        runDatesISO: a.runDatesISO,
        daysConfidence: a.daysConfidence,
        dayKind: a.dayKind,
        why: a.why,
        needsPerson: a.needsPerson === true,
        reasons: Array.isArray(a.reasons) ? a.reasons : [],
      },
    },
  };
}

/** Map cellKey → ghosts, from the open Inbox rows that carry ok FAV 2.0 advice. */
export function buildGhostsByCell(rows) {
  const map = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    const f = r && r.why && r.why.fav2;
    if (!f || f.status !== "ok" || !f.installer) continue;
    const days = ghostDays(f);
    days.forEach((iso, i) => {
      const key = ghostCellKey(f.installer, isoToMdy(iso));
      const ghost = {
        jobId: r.jobId,
        rowId: r.id,
        day: i + 1,
        of: days.length,
        crew: f.installerName || f.installer,
        confidence: f.daysConfidence ?? null,
        bookedDate: r.bookedDate,
        adviceOnly: r.adviceOnly === true, // not booked at all (#652)
      };
      map.set(key, [...(map.get(key) || []), ghost]);
    });
  }
  return map;
}
